/**
 * AnalyticsSyncService
 *
 * Responsible for:
 *  1. Interval-based sync (every 5 min by default)
 *  2. Condition-gated: only syncs when new/unsynced data exists
 *  3. Exponential back-off on consecutive failures
 *  4. Failed-event retry queue
 *  5. Online/offline awareness
 *  6. Immediate flush on page unload (sendBeacon)
 *  7. Manual trigger for force-sync
 */
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { AuthService, EntityService } from '@core/services';
import { environment } from '@env/environment';
import {
    EMPTY,
    fromEvent,
    merge,
    of,
    Subject,
    Subscription,
    timer,
} from 'rxjs';
import {
    catchError,
    filter,
    retry,
    switchMap,
    takeUntil,
    tap
} from 'rxjs/operators';

import { DEFAULT_ANALYTICS_CONFIG } from '../constants/analytics.constants';
import {
    AnalyticsConfig,
    AnalyticsEvent,
    AnalyticsSyncPayload,
    AnalyticsSyncResponse,
    FunnelSnapshot,
} from '../models/analytics.interfaces';
import { AnalyticsStorageService } from './analytics-storage.service';

@Injectable()
export class AnalyticsSyncService implements OnDestroy {
    private config: AnalyticsConfig = DEFAULT_ANALYTICS_CONFIG;
    private destroy$ = new Subject<void>();
    private manualSync$ = new Subject<void>();
    private syncSubscription: Subscription | null = null;
    private isSyncing = false;
    private apiUrl: string;
    private petpoojaUserLogged = false;

    // User/company context for payloads
    private userCtx: {
        userId: string;
        userName: string;
        userEmail: string;
        entityId: string;
        companyId: string;
        companyName: string;
    } = { userId: '', userName: '', userEmail: '', entityId: '', companyId: '', companyName: '' };

    constructor(
        private http: HttpClient,
        private storage: AnalyticsStorageService,
        private authService: AuthService,
        private entityService: EntityService
    ) {
        this.apiUrl =
            (environment as any).dashboard?.url +
            (environment as any).dashboard?.path;

        this.setupVisibilityListener();
        this.setupBeforeUnloadListener();
    }

    ngOnDestroy(): void {
        this.stopSync();
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ───────────── Configuration ──────────────

    configure(config: Partial<AnalyticsConfig>): void {
        this.config = { ...this.config, ...config };
    }

    setUserContext(ctx: {
        userId: string;
        userName: string;
        userEmail: string;
        entityId: string;
        companyId: string;
        companyName: string;
    }): void {
        this.userCtx = ctx;
        // Reset flag when user context changes
        if (this.isPetpoojaUser(ctx.userEmail)) {
            this.petpoojaUserLogged = false;
        }
    }

    // ───────────── Start / Stop ──────────────

    /**
     * Start the periodic sync loop.
     * Sync fires every `syncIntervalMs` but only if there is pending data.
     * Also listens for manual triggers.
     */
    startSync(): void {
        // Check if user is from petpooja.com domain
        if (this.isPetpoojaUser(this.userCtx.userEmail)) {
            if (!this.petpoojaUserLogged) {
                console.log('[PM Analytics] User from petpooja.com domain detected. Analytics tracking and sync disabled for this user.');
                this.petpoojaUserLogged = true;
            }
            return; // Don't start sync
        }

        if (this.syncSubscription) return;

        const interval$ = timer(this.config.syncIntervalMs, this.getSyncInterval());
        const trigger$ = merge(interval$, this.manualSync$);

        this.syncSubscription = trigger$
            .pipe(
                filter(() => !this.isSyncing),
                filter(() => navigator.onLine !== false),
                filter(() => this.storage.hasPendingData()),
                switchMap(() => this.executeSyncCycle()),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    stopSync(): void {
        this.syncSubscription?.unsubscribe();
        this.syncSubscription = null;
    }

    /**
     * Manual trigger (e.g., on navigate-away, logout).
     */
    triggerSync(): void {
        this.manualSync$.next();
    }

    /**
     * Blocking sync (returns Promise). Use sparingly, e.g., before logout.
     */
    async flushSync(): Promise<void> {
        if (!this.storage.hasPendingData()) return;
        try {
            await this.executeSyncCycle().toPromise();
        } catch (e) {
            console.warn('[PM Analytics] Flush sync failed:', e);
        }
    }

    // ───────────── Core Sync Cycle ──────────────

    private executeSyncCycle() {
        this.isSyncing = true;

        // Retry failed events first
        this.storage.retryFailedEvents();

        const events = this.storage.getPendingEvents(this.config.batchSize);
        if (events.length === 0) {
            this.isSyncing = false;
            return EMPTY;
        }

        const payload = this.buildPayload(events);

        const headers = this.buildRequestHeaders();

        return this.http
            .post<AnalyticsSyncResponse>(
                `${this.apiUrl}${this.config.syncEndpoint}`,
                payload,
                { headers }
            )
            .pipe(
                retry(1), // Single automatic retry on network error
                tap((response) => this.handleSyncSuccess(response, events)),
                catchError((error) => {
                    this.handleSyncFailure(events, error);
                    return of(null);
                }),
                tap(() => {
                    this.isSyncing = false;
                })
            );
    }

    private handleSyncSuccess(
        response: AnalyticsSyncResponse,
        events: AnalyticsEvent[]
    ): void {
        if (response.success) {
            const eventIds = events.map((e) => e.eventId);
            this.storage.markEventsSynced(eventIds);

            if (this.config.debugMode) {
                console.log(
                    `[PM Analytics] Synced ${events.length} events (batch: ${response.batchId})`
                );
            }

            // Partial failure handling
            if (response.failedEventIds?.length) {
                const failedEvents = events.filter((e) =>
                    response.failedEventIds!.includes(e.eventId)
                );
                this.storage.markEventsFailed(failedEvents);
            }
        } else {
            this.handleSyncFailure(events, new Error(response.error || 'Unknown'));
        }
    }

    private handleSyncFailure(events: AnalyticsEvent[], error: any): void {
        console.warn('[PM Analytics] Sync failed:', error?.message || error);
        this.storage.markEventsFailed(events);

        // Check storage size after failure accumulation
        if (this.storage.isStorageNearLimit()) {
            this.storage.emergencyCleanup();
        }
    }

    // ───────────── Payload Building ──────────────

    private buildPayload(events: AnalyticsEvent[]): AnalyticsSyncPayload {
        const sessionId = this.storage.getCurrentSessionId();
        const counters = this.storage.getSessionCounters();
        const funnelSnapshots = this.storage.getAllFunnelSnapshots();

        // Only include completed or dropped-off funnels
        const completedFunnels: FunnelSnapshot[] = Object.values(funnelSnapshots).filter(
            (f) => f.completed || f.dropOffStage !== null
        );

        // Extract and aggregate search keywords from search events
        const searchKeywords = this.extractSearchKeywords(events);

        // Extract and aggregate cart products from cart_add events
        const cartProducts = this.extractCartProducts(events);

        return {
            batchId: this.uuid(),
            syncTimestamp: new Date().toISOString(),
            userId: this.userCtx.userId || events[0]?.userId || '',
            userName: this.userCtx.userName || events[0]?.userName || '',
            userEmail: this.userCtx.userEmail || events[0]?.userEmail || '',
            entityId: this.userCtx.entityId || events[0]?.entityId || '',
            companyId: this.userCtx.companyId || events[0]?.companyId || '',
            companyName: this.userCtx.companyName || events[0]?.companyName || '',
            sessionId,
            clientInfo: {
                userAgent: navigator.userAgent,
                screenResolution: `${window.screen.width}x${window.screen.height}`,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                appVersion: (environment as any).appVersion || '1.0.0',
            },
            events,
            funnelSnapshots: completedFunnels,
            sessionCounters: counters,
            searchKeywords,
            cartProducts,
        };
    }

    /**
     * Extract and aggregate search keywords from search events.
     * Each unique keyword gets an entry with count, latest timestamp, and result info.
     */
    private extractSearchKeywords(
        events: AnalyticsEvent[]
    ): AnalyticsSyncPayload['searchKeywords'] {
        const keywordMap = new Map<string, {
            keyword: string;
            count: number;
            supplier: string;
            timestamp: string;
            resultCount: number;
            hasResults: boolean;
        }>();

        events.forEach((ev: any) => {
            if (ev.eventType !== 'search') return;
            const keyword = ev.data?.keyword || '';
            if (!keyword) return;

            const key = keyword.toLowerCase().trim();
            if (keywordMap.has(key)) {
                const existing = keywordMap.get(key)!;
                existing.count++;
                existing.resultCount = Math.max(existing.resultCount, ev.data?.resultCount || 0);
                existing.hasResults = existing.hasResults || (ev.data?.hasResults ?? false);
                if (ev.timestamp > existing.timestamp) {
                    existing.timestamp = ev.timestamp;
                    existing.supplier = ev.supplier || '-';
                }
            } else {
                keywordMap.set(key, {
                    keyword,
                    count: 1,
                    supplier: ev.supplier || '-',
                    timestamp: ev.timestamp || '',
                    resultCount: ev.data?.resultCount || 0,
                    hasResults: ev.data?.hasResults ?? false,
                });
            }
        });

        return Array.from(keywordMap.values()).sort((a, b) => b.count - a.count);
    }

    /**
     * Extract and aggregate cart products from cart_add events.
     * Each unique item gets an entry with total quantity, price, count, etc.
     */
    private extractCartProducts(
        events: AnalyticsEvent[]
    ): AnalyticsSyncPayload['cartProducts'] {
        const productMap = new Map<string, {
            itemId: string;
            itemName: string;
            quantity: number;
            unitPrice: number;
            itemUnit: string;
            supplier: string;
            timestamp: string;
            count: number;
        }>();

        events.forEach((ev: any) => {
            if (ev.eventType !== 'cart_add') return;
            const itemName = ev.data?.itemName || '';
            const itemId = ev.data?.itemId || '';
            if (!itemName) return;

            const key = (itemId || itemName).toLowerCase().trim();
            if (productMap.has(key)) {
                const existing = productMap.get(key)!;
                existing.quantity += (ev.data?.quantity || 0);
                existing.count++;
                if (ev.timestamp > existing.timestamp) {
                    existing.timestamp = ev.timestamp;
                    existing.supplier = ev.supplier || '-';
                }
            } else {
                productMap.set(key, {
                    itemId,
                    itemName,
                    quantity: ev.data?.quantity || 0,
                    unitPrice: ev.data?.unitPrice || 0,
                    itemUnit: ev.data?.itemUnit || '',
                    supplier: ev.supplier || '-',
                    timestamp: ev.timestamp || '',
                    count: 1,
                });
            }
        });

        return Array.from(productMap.values()).sort((a, b) => b.count - a.count);
    }

    // ───────────── Dynamic Interval with Back-off ──────────────

    private getSyncInterval(): number {
        const failures = this.storage.getConsecutiveFailures();
        if (failures === 0) return this.config.syncIntervalMs;

        // Exponential back-off, capped at 30 min
        const backoff =
            this.config.syncIntervalMs *
            Math.pow(this.config.backoffMultiplier, Math.min(failures, 5));
        return Math.min(backoff, 30 * 60 * 1000);
    }

    // ───────────── Visibility & Unload Listeners ──────────────

    /**
     * Trigger sync when tab becomes visible again (user returns).
     */
    private setupVisibilityListener(): void {
        fromEvent(document, 'visibilitychange')
            .pipe(
                filter(() => document.visibilityState === 'visible'),
                filter(() => this.storage.hasPendingData()),
                takeUntil(this.destroy$)
            )
            .subscribe(() => this.triggerSync());
    }

    /**
     * Use sendBeacon on page unload as a best-effort final sync.
     */
    private setupBeforeUnloadListener(): void {
        fromEvent(window, 'beforeunload')
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                if (!this.storage.hasPendingData()) return;

                const events = this.storage.getPendingEvents(this.config.batchSize);
                if (events.length === 0) return;

                const payload = this.buildPayload(events);

                try {
                    const headerMap = this.getAuthHeadersMap();
                    // Use fetch with keepalive instead of sendBeacon to include auth headers
                    fetch(`${this.apiUrl}${this.config.syncEndpoint}`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            ...headerMap,
                        },
                        body: JSON.stringify(payload),
                        keepalive: true,
                    }).then(() => {
                        // Best-effort: mark events synced (may not persist if page closes)
                        this.storage.markEventsSynced(events.map((e) => e.eventId));
                    }).catch(() => {
                        // fetch failed silently — events remain in storage for next session
                    });
                } catch {
                    // fetch failed silently — events remain in storage for next session
                }
            });
    }

    // ───────────── Utility ──────────────

    // ───────────── Auth Headers ──────────────

    /**
     * Build HttpHeaders with Authorization token and x-entity-ids
     * (same as HeaderInterceptor + TokenInterceptor do for other requests).
     */
    private buildRequestHeaders(): HttpHeaders {
        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
        });

        const token = this.authService.token;
        if (token) {
            headers = headers.set('Authorization', `Bearer ${token}`);
        }

        const entityId = this.getEntityIds();
        if (entityId) {
            headers = headers.set('x-entity-ids', entityId);
        }

        return headers;
    }

    /**
     * Plain object version of auth headers (used by fetch/keepalive on unload).
     */
    private getAuthHeadersMap(): Record<string, string> {
        const headersMap: Record<string, string> = {};

        const token = this.authService.token;
        if (token) {
            headersMap['Authorization'] = `Bearer ${token}`;
        }

        const entityId = this.getEntityIds();
        if (entityId) {
            headersMap['x-entity-ids'] = entityId;
        }

        return headersMap;
    }

    /**
     * Get selected entity ID string for the x-entity-ids header.
     */
    private getEntityIds(): string {
        const singleEntity = this.entityService.getSingleSelectedEntity();
        if (singleEntity?.id) {
            return singleEntity.id;
        }

        const entities = this.entityService.getSelectedEntities();
        if (entities?.length) {
            return entities
                .filter((e: any) => e.id !== '0')
                .map((e: any) => e.id)
                .toString();
        }

        return '';
    }

    private uuid(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }

    /**
     * Check if user email belongs to petpooja.com domain
     */
    private isPetpoojaUser(email: string): boolean {
        if (!email) return false;
        return email.toLowerCase().endsWith('@petpooja.com');
    }
}
